import { register } from "@shopify/theme-sections";
import { Loader } from "@googlemaps/js-api-loader";

const googleMapLoader = new Loader({
	apiKey: window.locatorApiKey,
	version: "weekly",
	libraries: ["geometry", "places"]
});

const selectors = {
	googleMap: "[data-store-locations-map]",
	storeLocationsList: "[data-store-locations-list]",
	storeLocationItem: "[data-store-location]",
	storeLocationsLoadMore: "[data-store-locations-load-more]",
	filterForm: "[data-filter-form]",
	filterAddress: "[data-filter-address]",
	filterRadius: "[data-filter-radius]",
	storeLocationsOfficialStores: "[data-filter-official-stores]",
	storeLocationsResellers: "[data-filter-reseller-stores]",
	storeNotFound: "[data-store-not-found]",
	markerImage: "[data-marker-image]",
	resetStore: "[data-store-reset]"
};

const modifiers = {
	initialized: "initialized"
};

register("store-locations", {
	mapInstance: null,
	geocoderInstance: null,
	infoWindowInstance: null,
	markerInstance: null,
	circleInstance: null,
	filters: {
		official: false,
		resellers: false
	},
	stores: [],
	markers: [],
	officialStores: [],
	resellerStores: [],
	filteredStores: [],
	mapContainer: null,
	storeLocationsListContainer: null,
	storeLocationsLoadMore: null,
	isLoadedMore: false,
	filterForm: null,
	filterAddress: null,
	filterRadius: null,
	officialStoresFilter: null,
	resellerStoresFilter: null,
	markerImage: null,
	storeNotFound: null,
	loadedMore: false,
	defaultCenter: {
		lat: 51.1657,
		lng: 10.4515
	},
	autocompleteOptions: {
		fields: ["formatted_address", "geometry", "name"]
	},
	onLoad: function () {
		this.init();
	},
	init: function () {
		this.initElements();
		this.initEvents();
		this.initMap();
		this.renderStores();
	},
	initElements: function () {
		this.mapContainer = this.container.querySelector(selectors.googleMap);
		this.storeLocationsListContainer = this.container.querySelector(
			selectors.storeLocationsList
		);
		this.storeLocationsLoadMore = this.container.querySelector(
			selectors.storeLocationsLoadMore
		);
		this.markerImage = this.container.querySelector(selectors.markerImage);

		this.filterAddress = this.container.querySelector(
			selectors.filterAddress
		);
		this.filterRadius = this.container.querySelector(
			selectors.filterRadius
		);
		this.filterForm = this.container.querySelector(selectors.filterForm);
		this.officialStoresFilter = this.container.querySelector(
			selectors.storeLocationsOfficialStores
		);
		this.resellerStoresFilter = this.container.querySelector(
			selectors.storeLocationsResellers
		);
		this.storeNotFound = this.container.querySelector(
			selectors.storeNotFound
		);
		this.resetStore = this.container.querySelector(selectors.resetStore);
	},
	initMap: function () {
		if (!this.mapContainer || !window.locatorApiKey.length) {
			return null;
		}
		let self = this;

		googleMapLoader.load().then(() => {
			const autocomplete = new window.google.maps.places.Autocomplete(
				this.filterAddress,
				this.autocompleteOptions
			);
			this.mapInstance = new window.google.maps.Map(this.mapContainer, {
				mapId: "fcc230106537e2b",
				center: self.defaultCenter,
				zoom: 10,
				disableDefaultUI: true
			});
			this.container.classList.add(modifiers.initialized);
			this.geocoderInstance = new window.google.maps.Geocoder();
			this.infoWindowInstance = new window.google.maps.InfoWindow();
			this.initMarkers();
			autocomplete.bindTo("bounds", this.mapInstance);
			autocomplete.addListener("place_changed", () => {
				const place = autocomplete.getPlace();
				this.mapInstance.setCenter(place.geometry.location);
			});
		});
	},
	createMarker: function (markerData, storeData) {
		let self = this;
		let markerImageSvg = self.markerImage.innerHTML || "";

		const marker = new window.google.maps.Marker({
			position: markerData.latLng,
			title: storeData.title,
			icon: {
				anchor: new window.google.maps.Point(16, 16),
				url:
					"data:image/svg+xml;charset=utf-8," +
					encodeURIComponent(markerImageSvg)
			}
		});
		const markerContent = `<div class="store-marker">
			<h3 class="store-locations__list-item-title">${storeData.title}</h3>
			${
				storeData.address
					? '<a href="https://www.google.com/maps/search/' +
					  encodeURI(storeData.address) +
					  '" target="_blank">' +
					  storeData.address +
					  "</a>"
					: ""
			}
			${
				storeData.phone
					? '<p>Phone: <a href="tel:' +
					  storeData.phone +
					  '">' +
					  storeData.phone +
					  "</a></p>"
					: ""
			}
			${
				storeData.email
					? '<p>Email: <a href="mailto:' +
					  storeData.email +
					  '">' +
					  storeData.email +
					  "</a></p>"
					: ""
			}
			${
				storeData.workingHours
					? "<p>Working Hours: " + storeData.workingHours + "</p>"
					: ""
			}
		</div>`;

		window.google.maps.event.addListener(marker, "click", function () {
			self.infoWindowInstance.setContent(markerContent);
			self.infoWindowInstance.open(self.mapInstance, marker);
		});

		marker.storeData = storeData;

		marker.setMap(this.mapInstance);
		this.markers.push(marker);
	},
	initEvents: function () {
		let self = this;

		this.filterForm.addEventListener(
			"submit",
			function (e) {
				e.preventDefault();

				this.handleMarkers(this.filterMarkers());
			}.bind(this)
		);

		if (this.filterAddress !== null) {
			this.filterAddress.addEventListener(
				"change",
				window.debounce(() => {
					if (this.filterAddress.value.length) {
						this.handleMarkers(this.filterMarkers());
					} else {
						this.resetForm();
					}
				}, 300)
			);
		}

		if (this.filterRadius !== null) {
			this.filterRadius.addEventListener(
				"change",
				window.debounce(() => {
					if (this.filterAddress.value.length) {
						this.handleMarkers(this.filterMarkers());
					}
				}, 300)
			);
		}

		if (this.officialStoresFilter !== null) {
			this.officialStoresFilter.addEventListener(
				"change",
				window.debounce(() => {
					this.filters.official = this.officialStoresFilter.checked;
					this.initMarkers();
				}, 300)
			);
		}
		if (this.resellerStoresFilter !== null) {
			this.resellerStoresFilter.addEventListener(
				"change",
				window.debounce(() => {
					this.filters.resellers = this.resellerStoresFilter.checked;
					this.initMarkers();
				}, 300)
			);
		}

		if (this.storeLocationsLoadMore !== null) {
			this.storeLocationsLoadMore.addEventListener("click", function () {
				self.storeLocationsListContainer.classList.add("load-more");
				self.loadedMore = true;
				this.classList.add("hide");
			});
		} else {
			this.storeLocationsListContainer.classList.add("show-all");
		}

		for (let item of this.storeLocationsListContainer.children) {
			item.addEventListener(
				"click",
				function () {
					window.google.maps.event.trigger(
						this.getMarkerById(item.id)[0],
						"click"
					);
					if (window.innerWidth < window.innerHeight) {
						window.scrollTo({ top: 0, behavior: "smooth" });
					}
				}.bind(this)
			);
		}

		this.resetStore.addEventListener(
			"click",
			function () {
				this.resetForm();
			}.bind(this)
		);
	},
	resetForm: function () {
		this.mapInstance.setCenter(this.defaultCenter);
		this.mapInstance.setZoom(7);
		this.filterAddress.value = "";
		this.filterRadius.value = "";
		this.resellerStoresFilter.checked = false;
		this.officialStoresFilter.checked = false;
		this.filters.official = false;
		this.filters.resellers = false;

		this.handleMarkers(this.filterMarkers());
	},
	getMarkerById: function (id) {
		return this.markers.filter((marker) => marker.storeData.id === id);
	},
	clearMarkers: function () {
		for (let i = 0; i < this.markers.length; i++) {
			this.markers[i].setMap(null);
		}
		this.markers = [];
	},
	initMarkers: function () {
		this.stores = window.storeLocations;

		let locationList = [
			...this.container.querySelectorAll(selectors.storeLocationItem)
		].filter((item) => item.dataset.locations);

		if (this.filters.official && !this.filters.resellers) {
			locationList = [...locationList].filter((element) => {
				return (
					element.dataset.storeType.toLowerCase() === "official store"
				);
			});
			this.clearMarkers();
			const locationsIndexes = locationList.map(
				(item) => +item.id.replace("store-", "")
			);
			locationsIndexes.forEach((value, index) => {
				const myLatlng = new window.google.maps.LatLng(
					...locationList[index].dataset.locations
						.split(",")
						.map((value) => Number(value))
				);
				const markerData = {
					latLng: myLatlng
				};
				this.createMarker(markerData, this.stores[value]);
				if (index === 0) {
					this.mapInstance.setCenter(myLatlng);
				}
			});
			return;
		} else if (!this.filters.official && this.filters.resellers) {
			locationList = [...locationList].filter((element) => {
				return element.dataset.storeType.toLowerCase() === "reseller";
			});
			this.clearMarkers();

			const locationsIndexes = locationList.map(
				(item) => +item.id.replace("store-", "")
			);

			locationsIndexes.forEach((value, index) => {
				const myLatlng = new window.google.maps.LatLng(
					...locationList[index].dataset.locations
						.split(",")
						.map((value) => Number(value))
				);
				const markerData = {
					latLng: myLatlng
				};
				this.createMarker(markerData, this.stores[value]);
				if (index === 0) {
					this.mapInstance.setCenter(myLatlng);
				}
			});
			return;
		}

		const locationsIndexes = locationList.map(
			(item) => +item.id.replace("store-", "")
		);

		locationsIndexes.forEach((value, index) => {
			const myLatlng = new window.google.maps.LatLng(
				...locationList[value].dataset.locations
					.split(",")
					.map((value) => Number(value))
			);
			const markerData = {
				latLng: myLatlng
			};
			this.createMarker(markerData, this.stores[value]);
			if (index === 0) {
				this.mapInstance.setCenter(myLatlng);
			}
		});
	},
	filterMarkers: function () {
		let filtered = this.markers;
		if (this.filters.official && !this.filters.resellers) {
			filtered = this.markers.filter(
				(marker) => marker.storeData.type === "Official Store"
			);
		} else if (!this.filters.official && this.filters.resellers) {
			filtered = this.markers.filter(
				(marker) => marker.storeData.type === "Reseller"
			);
		}
		return filtered;
	},
	hideMarkers: function () {
		this.markers.forEach((marker) => {
			marker.setMap(null);
			document.getElementById(marker.storeData.id).classList.add("hide");
		});
	},
	renderStores: function (options = "") {
		let storeListChildren = this.storeLocationsListContainer.children;

		if (options !== "filtered") {
			for (const child of storeListChildren) {
				child.classList.remove("hide");
			}
		} else {
			for (const child of storeListChildren) {
				child.classList.add("hide");
			}
			this.filteredStores.forEach((store) => {
				document.getElementById(store.id).classList.remove("hide");
			});
		}
	},
	handleSideBar: function (foundMarkers) {
		this.storeNotFound.classList.add("hide");
		this.resetStore.classList.add("hide");

		if (foundMarkers === 0) {
			this.storeNotFound.classList.remove("hide");
			this.resetStore.classList.remove("hide");
			this.storeLocationsLoadMore.classList.add("hide");
		} else if (foundMarkers > 0 && foundMarkers <= 2) {
			this.storeLocationsLoadMore.classList.add("hide");
			this.storeLocationsListContainer.classList.add("show-all");
		} else {
			if (!this.loadedMore) {
				this.storeLocationsLoadMore.classList.remove("hide");
			}
		}
	},
	handleMarkers: function (_markers) {
		let self = this;
		let address =
			this.filterAddress !== null ? this.filterAddress.value : "";
		let radius = this.filterRadius.value.length
			? parseInt(this.filterRadius.value, 10) * 1000
			: parseInt("10", 10) * 1000;
		let bounds = new window.google.maps.LatLngBounds();
		let spherical = window.google.maps.geometry.spherical;
		let foundMarkers = 0;

		_markers = _markers || this.markers;
		self.hideMarkers();

		if (address.length && radius) {
			self.geocoderInstance.geocode(
				{ address: address },
				function (results, status) {
					if (status === window.google.maps.GeocoderStatus.OK) {
						let searchCenter = results[0].geometry.location;

						self.mapInstance.setCenter(searchCenter);
						if (self.circleInstance) {
							self.circleInstance.setMap(null);
						}

						self.circleInstance = new window.google.maps.Circle({
							center: searchCenter,
							radius: radius,
							strokeWeight: 1,
							fillOpacity: 0.1,
							fillColor: "#051f41",
							map: self.mapInstance
						});

						for (let i = 0; i < _markers.length; i++) {
							if (
								spherical.computeDistanceBetween(
									_markers[i].getPosition(),
									searchCenter
								) < radius
							) {
								bounds.extend(_markers[i].getPosition());
								_markers[i].setMap(self.mapInstance);
								document
									.getElementById(_markers[i].storeData.id)
									.classList.remove("hide");
								foundMarkers++;
							} else {
								document
									.getElementById(_markers[i].storeData.id)
									.classList.add("hide");
								_markers[i].setMap(null);
							}
						}

						self.mapInstance.fitBounds(
							self.circleInstance.getBounds()
						);

						self.handleSideBar(foundMarkers);
					} else {
						console.log(
							"Geocode was not successful for the following reason: " +
								status
						);
					}
				}
			);
		} else {
			this.circleInstance.setMap(null);

			for (let i = 0; i < _markers.length; i++) {
				if (_markers.length === 1) {
					self.mapInstance.setCenter(_markers[0].getPosition());
				}

				bounds.extend(_markers[i].getPosition());
				_markers[i].setMap(self.mapInstance);
				document
					.getElementById(_markers[i].storeData.id)
					.classList.remove("hide");
				foundMarkers++;
			}

			self.handleSideBar(foundMarkers);
		}
	}
});
